/**
 * ContinuityPlansView (P401.1). Route: /twin/continuity-plans.
 *
 * BCP and ITDR plan management, and running one as a playbook during a
 * live event.
 *
 * An ACTIVE activation leads the page. During a live event it is the
 * only thing that matters, and a plan library above it would be
 * furniture between an operator and the step they are on.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { canRunOperations } from "shared/api/authority";
import { MarsadIcon } from "shared/components/MarsadIcon";

import { formatDate } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useToast } from "shared/toast/ToastProvider";
import { refusalMessage } from "shared/api/refusalMessage";

import { LessonsRegister } from "./LessonsRegister";
import { usePlanTests, useRaiseLesson, useRecordPlanTest } from "./continuityTestHooks";

interface PlanRow {
  id: string;
  code: string;
  title: string;
  kind: "bcp" | "itdr";
  status: "draft" | "approved" | "retired";
  owner_role: string | null;
  generated_from: string | null;
  last_activated_at: string | null;
  /** Round 14 D4. */
  approved_at?: string | null;
  last_tested_at?: string | null;
  next_test_due?: string | null;
}

interface ActivationStep {
  step_number: number;
  title: string;
  outcome: "pending" | "done" | "skipped" | "blocked";
  expected_minutes: number | null;
  actual_minutes: number | null;
  note: string | null;
  completed_at: string | null;
}

interface ActivationRow {
  id: string;
  plan_id: string;
  plan_title: string | null;
  kind: string | null;
  status: "active" | "completed" | "abandoned";
  reason: string;
  activated_at: string | null;
  completed_at: string | null;
  steps_total: number;
  steps_done: number;
  steps: ActivationStep[];
}

const OUTCOME_TONE: Record<string, string> = {
  pending: "bg-slate-100 text-slate-600",
  done: "bg-emerald-100 text-emerald-800",
  skipped: "bg-slate-200 text-slate-700",
  blocked: "bg-rose-100 text-rose-800",
};

const OUTCOMES = ["done", "skipped", "blocked"] as const;

export function ContinuityPlansView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  /*
   * Round 10 F1 -- who this page is for.
   *
   * Every write here is gated by `require_opsres_or_admin`, which
   * admits the Head of Operational Resilience: continuity is her work,
   * and activating a plan during an event is the most obviously hers
   * on the surface. This screen alone still asked for `is_admin`, so
   * she was shown the standfirst promising a plan generated from the
   * recovery sequence above a page with no button to generate it, no
   * way to activate one and no way to record a step -- prose where the
   * product should have been. Every sibling ceremony view (drills,
   * scenarios, MVB candidates, IBS admission) already mirrors the
   * backend gate; this one was missed.
   */
  const canOperate = canRunOperations(me.data);

  const plans = useQuery({
    queryKey: ["twin", "continuity-plans"],
    queryFn: () => api.get<{ plans: PlanRow[] }>("/v1/twin/continuity-plans"),
  });
  const activations = useQuery({
    queryKey: ["twin", "plan-activations"],
    queryFn: () =>
      api.get<{ activations: ActivationRow[] }>("/v1/twin/plan-activations"),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["twin", "plan-activations"] });
    void qc.invalidateQueries({ queryKey: ["twin", "continuity-plans"] });
  };

  const generate = useMutation({
    mutationFn: () =>
      api.post("/v1/admin/twin/continuity-plans/generate", {
        code: `itdr.mvb.${Date.now()}`,
        title: "ITDR — MVB restoration (generated)",
      }),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: (vars: { planId: string; reason: string }) =>
      api.post(`/v1/admin/twin/continuity-plans/${vars.planId}/activate`, {
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });

  const setStep = useMutation({
    mutationFn: (vars: {
      activationId: string;
      step: number;
      outcome: string;
      note?: string | undefined;
    }) =>
      api.post(
        `/v1/admin/twin/plan-activations/${vars.activationId}/steps/${vars.step}`,
        { outcome: vars.outcome, note: vars.note ?? null },
      ),
    onSuccess: invalidate,
  });

  const close = useMutation({
    mutationFn: (vars: { activationId: string; status: string }) =>
      api.post(`/v1/admin/twin/plan-activations/${vars.activationId}/close`, {
        status: vars.status,
      }),
    onSuccess: invalidate,
  });

  const all = activations.data?.activations ?? [];
  const live = all.filter((a) => a.status === "active");
  const past = all.filter((a) => a.status !== "active");

  return (
    <article data-testid="continuity-plans-view" className="max-w-4xl space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="drill" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("continuityPlans.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("continuityPlans.body")}</p>
      </header>

      {live.length > 0 && (
        <section data-testid="continuity-plans-live" className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-wide text-rose-700">
            {t("continuityPlans.liveHeading", { count: live.length })}
          </h2>
          {live.map((a) => (
            <ActivationRunner
              key={a.id}
              activation={a}
              canOperate={canOperate}
              onSetStep={(step, outcome, note) =>
                setStep.mutate({ activationId: a.id, step, outcome, note })
              }
              onClose={(status) => close.mutate({ activationId: a.id, status })}
            />
          ))}
        </section>
      )}

      <section data-testid="continuity-plans-library" className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
            {t("continuityPlans.libraryHeading")}
          </h2>
          {canOperate && (
            <button
              type="button"
              data-testid="continuity-plans-generate"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
            >
              {t("continuityPlans.generate")}
            </button>
          )}
        </div>
        {(plans.data?.plans ?? []).length === 0 && (
          <p className="text-sm text-slate-500">{t("continuityPlans.noPlans")}</p>
        )}
        <ul className="space-y-1.5">
          {(plans.data?.plans ?? []).map((p) => (
            <li
              key={p.id}
              data-testid={`continuity-plan-${p.code}`}
              className="rounded border border-paper-2 bg-white p-2.5"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs uppercase text-slate-600">
                  {p.kind}
                </span>
                <span className="font-medium text-slate-900">{p.title}</span>
                <span className="text-xs text-slate-400">{p.code}</span>
                <span className="rounded bg-paper-2 px-1.5 py-0.5 text-xs text-slate-600">
                  {p.status}
                </span>
                {/* A generated plan must never pass as a considered one. */}
                {p.generated_from && (
                  <span
                    data-testid={`continuity-plan-generated-${p.code}`}
                    className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                  >
                    {t("continuityPlans.generatedBadge")}
                  </span>
                )}
              </div>
              <PlanTestsLine plan={p} />
              <PlanTests plan={p} canOperate={canOperate} />
              {canOperate && p.status !== "retired" && (
                <ActivateControl
                  planCode={p.code}
                  pending={activate.isPending}
                  onActivate={(reason) => activate.mutate({ planId: p.id, reason })}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* P420 -- what the exercises taught, and what happened to it. */}
      <LessonsRegister />

      {past.length > 0 && (
        <section data-testid="continuity-plans-history" className="space-y-1.5">
          <h2 className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
            {t("continuityPlans.historyHeading")}
          </h2>
          <ul className="space-y-1">
            {past.map((a) => (
              <li
                key={a.id}
                className="rounded border border-paper-2 bg-white px-2.5 py-1.5 text-sm"
              >
                <span className="font-medium text-slate-900">{a.plan_title}</span>
                <span className="text-slate-500">
                  {" — "}
                  {t("continuityPlans.pastSummary", {
                    status: a.status,
                    done: a.steps_done,
                    total: a.steps_total,
                  })}
                </span>
                <p className="text-xs text-slate-500">{a.reason}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function ActivateControl({
  planCode,
  pending,
  onActivate,
}: {
  planCode: string;
  pending: boolean;
  onActivate: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <input
        dir="auto"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid={`continuity-plan-reason-${planCode}`}
        placeholder={t("continuityPlans.reasonPlaceholder")}
        className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
      />
      <button
        type="button"
        data-testid={`continuity-plan-activate-${planCode}`}
        // An activation without a stated reason is a record that cannot
        // be read back six months later.
        disabled={!reason.trim() || pending}
        onClick={() => onActivate(reason.trim())}
        className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {t("continuityPlans.activate")}
      </button>
    </div>
  );
}

function ActivationRunner({
  activation,
  canOperate,
  onSetStep,
  onClose,
}: {
  activation: ActivationRow;
  /* Named for what it means rather than for the admin flag it used
     to read: continuity is the Resilience Lead's work. */
  canOperate: boolean;
  onSetStep: (step: number, outcome: string, note?: string) => void;
  onClose: (status: string) => void;
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<Record<number, string>>({});

  return (
    <div
      data-testid={`activation-runner-${activation.id}`}
      className="rounded border-2 border-rose-200 bg-white p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-800">
          {t("continuityPlans.activeBadge")}
        </span>
        <span className="font-medium text-slate-900">{activation.plan_title}</span>
        <span
          data-testid="activation-progress"
          className="text-xs tabular-nums text-slate-500"
        >
          {t("continuityPlans.progress", {
            done: activation.steps_done,
            total: activation.steps_total,
          })}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-700">{activation.reason}</p>

      <ol className="mt-2 space-y-1.5">
        {activation.steps.map((s) => (
          <li
            key={s.step_number}
            data-testid={`activation-step-${s.step_number}`}
            data-outcome={s.outcome}
            className="rounded bg-paper-2 p-2"
          >
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-xs ${OUTCOME_TONE[s.outcome]}`}
              >
                {t(`continuityPlans.outcome.${s.outcome}`)}
              </span>
              <span className="text-sm text-slate-900">
                {s.step_number}. {s.title}
              </span>
              <span className="text-xs tabular-nums text-slate-500">
                {s.expected_minutes !== null &&
                  t("continuityPlans.expected", { minutes: s.expected_minutes })}
                {s.actual_minutes !== null &&
                  ` · ${t("continuityPlans.actual", { minutes: s.actual_minutes })}`}
              </span>
            </div>
            {s.note && <p className="mt-1 text-xs italic text-slate-600">{s.note}</p>}
            {canOperate && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <input
                  dir="auto"
                  type="text"
                  value={notes[s.step_number] ?? ""}
                  onChange={(e) =>
                    setNotes((n) => ({ ...n, [s.step_number]: e.target.value }))
                  }
                  data-testid={`activation-note-${s.step_number}`}
                  placeholder={t("continuityPlans.notePlaceholder")}
                  className="flex-1 rounded border border-slate-300 px-2 py-0.5 text-xs"
                />
                {OUTCOMES.map((o) => (
                  <button
                    key={o}
                    type="button"
                    data-testid={`activation-step-${s.step_number}-${o}`}
                    onClick={() => onSetStep(s.step_number, o, notes[s.step_number])}
                    className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:border-brand-brass"
                  >
                    {t(`continuityPlans.outcome.${o}`)}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>

      {canOperate && (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            data-testid="activation-complete"
            onClick={() => onClose("completed")}
            className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
          >
            {t("continuityPlans.complete")}
          </button>
          <button
            type="button"
            data-testid="activation-abandon"
            onClick={() => onClose("abandoned")}
            className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700"
          >
            {t("continuityPlans.abandon")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Round 14 D4 -- approved when, last tested when, next due when.
 *
 *  The plan read "approved" with no approval date behind it, and when a
 *  plan was last tested was on no screen for any seat. */
function PlanTestsLine({ plan }: { plan: PlanRow }) {
  const { t } = useTranslation();
  const lang = i18n.language;
  return (
    <p
      data-testid={`continuity-plan-dates-${plan.code}`}
      className="mt-1 text-xs text-slate-600"
    >
      {plan.status === "approved" &&
        (plan.approved_at
          ? t("continuityPlans.tests.approvedOn", {
              when: formatDate(plan.approved_at, lang),
            })
          : t("continuityPlans.tests.approvedUndated"))}
      {plan.status === "approved" && " · "}
      {plan.last_tested_at
        ? t("continuityPlans.tests.lastTested", {
            when: formatDate(plan.last_tested_at, lang),
          })
        : t("continuityPlans.tests.neverTested")}
      {plan.next_test_due &&
        ` · ${t("continuityPlans.tests.nextDue", {
          when: formatDate(plan.next_test_due, lang),
        })}`}
    </p>
  );
}

const TEST_METHODS = ["walkthrough", "tabletop", "simulation", "live"] as const;
const TEST_OUTCOMES = ["effective", "gaps_found", "ineffective"] as const;

/** Round 14 D4 -- the plan's tests, and a form to record one.
 *
 *  Plan tests and lessons had endpoints and no screen for any seat. */
function PlanTests({ plan, canOperate }: { plan: PlanRow; canOperate: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const tests = usePlanTests(open ? plan.id : null);
  const record = useRecordPlanTest(plan.id);
  const raise = useRaiseLesson();
  const [method, setMethod] = useState<string>("tabletop");
  const [outcome, setOutcome] = useState<string>("effective");
  const [notes, setNotes] = useState("");
  const [lessonFor, setLessonFor] = useState<string | null>(null);
  const [lesson, setLesson] = useState("");
  const rows = Array.isArray(tests.data) ? tests.data : [];

  return (
    <div className="mt-1">
      <button
        type="button"
        data-testid={`continuity-plan-tests-toggle-${plan.code}`}
        onClick={() => setOpen((o) => !o)}
        className="text-xs text-brand-brassDark underline decoration-dotted underline-offset-2"
      >
        {open ? t("continuityPlans.tests.hide") : t("continuityPlans.tests.show")}
      </button>
      {open && (
        <div
          data-testid={`continuity-plan-tests-${plan.code}`}
          className="mt-1.5 space-y-2 rounded bg-paper-2 p-2 text-xs"
        >
          {tests.isError ? (
            <p role="alert" className="text-rose-800">
              {t("continuityPlans.tests.loadFailed")}
            </p>
          ) : rows.length === 0 ? (
            <p className="text-slate-500">{t("continuityPlans.tests.none")}</p>
          ) : (
            <ul className="space-y-1">
              {rows.map((r) => (
                <li key={r.id} data-testid="plan-test-row">
                  <span className="text-slate-800">
                    {t("continuityPlans.tests.row", {
                      when: formatDate(r.tested_at, i18n.language),
                      method: t(`continuityPlans.tests.method.${r.method}`),
                      outcome: t(`continuityPlans.tests.outcome.${r.outcome}`),
                    })}
                  </span>
                  {r.notes && <span className="text-slate-500"> -- {r.notes}</span>}{" "}
                  {canOperate &&
                    (lessonFor === r.id ? (
                      <span className="ms-2 inline-flex gap-1">
                        <input
                          dir="auto"
                          aria-label={t("continuityPlans.tests.lessonLabel")}
                          data-testid="plan-test-lesson-input"
                          value={lesson}
                          onChange={(e) => setLesson(e.target.value)}
                          className="w-56 rounded border border-slate-300 px-1.5 py-0.5"
                        />
                        <button
                          type="button"
                          data-testid="plan-test-lesson-save"
                          disabled={!lesson.trim() || raise.isPending}
                          onClick={() =>
                            raise.mutate(
                              {
                                source_kind: "plan_test",
                                source_id: r.id,
                                lesson: lesson.trim(),
                              },
                              {
                                onSuccess: () => {
                                  setLessonFor(null);
                                  setLesson("");
                                  toast.push({
                                    tone: "success",
                                    message: t("continuityPlans.tests.lessonRaised"),
                                  });
                                },
                              },
                            )
                          }
                          className="text-brand-brassDark underline disabled:opacity-40"
                        >
                          {t("continuityPlans.tests.lessonSave")}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid="plan-test-raise-lesson"
                        onClick={() => setLessonFor(r.id)}
                        className="ms-2 text-brand-brassDark underline decoration-dotted underline-offset-2"
                      >
                        {t("continuityPlans.tests.raiseLesson")}
                      </button>
                    ))}
                </li>
              ))}
            </ul>
          )}
          {canOperate && (
            <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-2">
              <label className="flex flex-col">
                {t("continuityPlans.tests.methodLabel")}
                <select
                  data-testid="plan-test-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="rounded border border-slate-300 px-1 py-0.5"
                >
                  {TEST_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`continuityPlans.tests.method.${m}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col">
                {t("continuityPlans.tests.outcomeLabel")}
                <select
                  data-testid="plan-test-outcome"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  className="rounded border border-slate-300 px-1 py-0.5"
                >
                  {TEST_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {t(`continuityPlans.tests.outcome.${o}`)}
                    </option>
                  ))}
                </select>
              </label>
              <input
                dir="auto"
                aria-label={t("continuityPlans.tests.notesLabel")}
                placeholder={t("continuityPlans.tests.notesLabel")}
                data-testid="plan-test-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex-1 rounded border border-slate-300 px-1.5 py-0.5"
              />
              <button
                type="button"
                data-testid="plan-test-record"
                disabled={record.isPending}
                onClick={() =>
                  record.mutate(
                    {
                      method,
                      outcome,
                      ...(notes.trim() ? { notes: notes.trim() } : {}),
                    },
                    {
                      onSuccess: () => {
                        setNotes("");
                        toast.push({
                          tone: "success",
                          message: t("continuityPlans.tests.recorded"),
                        });
                      },
                    },
                  )
                }
                className="rounded bg-brand-brass px-2 py-0.5 font-medium text-brand-navy disabled:opacity-50"
              >
                {t("continuityPlans.tests.record")}
              </button>
              {record.isError && (
                <p role="alert" className="w-full text-rose-800">
                  {refusalMessage(t, record.error)}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
